import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, AlertTriangle, CheckCircle, X } from 'lucide-react';
import Button from '../shared/Button';
import ModalShell from '../shared/ModalShell';

const VendorVerificationModal = ({ isOpen, onClose, isPending = false }) => {
 const navigate = useNavigate();

 const handleVerifyNow = () => {
 onClose();
 navigate('/vendor/verification');
 };

 return (
 <ModalShell
 isOpen={isOpen}
 onClose={onClose}
 labelledBy="vendor-verification-title"
 panelClassName="max-w-md"
 >
 <div className="bg-surface rounded-lg shadow-sm border border-border overflow-hidden">
 {/* Header */}
 <div className="flex items-center justify-between p-6 border-b border-border">
 <div className="flex items-center space-x-3">
 <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
 <Shield className="w-5 h-5 text-amber-600" />
 </div>
 <div>
 <h3 id="vendor-verification-title" className="text-lg font-semibold text-ink">
 {isPending ? 'Application Pending Approval' : 'Account Verification Required'}
 </h3>
 <p className="text-sm text-ink-tertiary">
 {isPending ? 'Your verification is being reviewed by our team' : 'Complete verification to access all features'}
 </p>
 </div>
 </div>
 <button
 type="button"
 onClick={onClose}
 className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-surface-muted transition-colors"
 aria-label="Close"
 >
 <X className="w-5 h-5" />
 </button>
 </div>

 {/* Content */}
 <div className="p-6">
 <div className="flex items-start space-x-3 mb-6">
 <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${ isPending ? 'bg-surface-muted' : 'bg-red-100' }`}>
 {isPending ? (
 <CheckCircle className="w-4 h-4 text-ink" />
 ) : (
 <AlertTriangle className="w-4 h-4 text-red-600" />
 )}
 </div>
 <div>
 <h4 className="text-sm font-medium text-ink mb-1">
 {isPending ? 'Application Submitted' : 'Verification Required'}
 </h4>
 <p className="text-sm text-ink-secondary">
 {isPending 
 ? 'Your verification application has been submitted and is currently under review. You will receive an email notification once the review is complete.'
 : 'Your vendor account needs to be verified before you can access admin features and manage your business profile.'
 }
 </p>
 </div>
 </div>

 {/* Verification Steps or Status */}
 {isPending ? (
 <div className="space-y-3 mb-6">
 <div className="flex items-center space-x-3">
 <div className="w-6 h-6 bg-green-100 rounded-xl flex items-center justify-center">
 <CheckCircle className="w-3 h-3 text-green-600" />
 </div>
 <span className="text-sm text-ink-secondary">Application submitted successfully</span>
 </div>
 <div className="flex items-center space-x-3">
 <div className="w-6 h-6 bg-surface-muted rounded-xl flex items-center justify-center">
 <span className="text-xs font-medium text-ink">⏳</span>
 </div>
 <span className="text-sm text-ink-secondary">Under review by admin team</span>
 </div>
 <div className="flex items-center space-x-3">
 <div className="w-6 h-6 bg-gray-100 rounded-xl flex items-center justify-center">
 <span className="text-xs font-medium text-ink-secondary">📧</span>
 </div>
 <span className="text-sm text-ink-secondary">Email notification will be sent</span>
 </div>
 </div>
 ) : (
 <div className="space-y-3 mb-6">
 <div className="flex items-center space-x-3">
 <div className="w-6 h-6 bg-gray-100 rounded-xl flex items-center justify-center">
 <span className="text-xs font-medium text-ink-secondary">1</span>
 </div>
 <span className="text-sm text-ink-secondary">Upload business documents</span>
 </div>
 <div className="flex items-center space-x-3">
 <div className="w-6 h-6 bg-gray-100 rounded-xl flex items-center justify-center">
 <span className="text-xs font-medium text-ink-secondary">2</span>
 </div>
 <span className="text-sm text-ink-secondary">Provide business information</span>
 </div>
 <div className="flex items-center space-x-3">
 <div className="w-6 h-6 bg-gray-100 rounded-xl flex items-center justify-center">
 <span className="text-xs font-medium text-ink-secondary">3</span>
 </div>
 <span className="text-sm text-ink-secondary">Wait for admin approval</span>
 </div>
 </div>
 )}

 {/* Benefits */}
 <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
 <div className="flex items-start space-x-3">
 <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
 <div>
 <h5 className="text-sm font-medium text-green-900 mb-1">
 Benefits of Verification
 </h5>
 <ul className="text-xs text-green-700 space-y-1">
 <li>• Access to all admin features</li>
 <li>• Manage your business profile</li>
 <li>• Receive booking requests</li>
 <li>• Build customer trust</li>
 </ul>
 </div>
 </div>
 </div>

 {/* Actions */}
 <div className="flex space-x-3">
 {isPending ? (
 <Button
 onClick={onClose}
 variant="primary"
 className="flex-1 rounded-md"
 >
 Understood
 </Button>
 ) : (
 <>
 <Button
 onClick={handleVerifyNow}
 variant="primary"
 className="flex-1 rounded-md"
 >
 Start Verification
 </Button>
 <Button
 onClick={onClose}
 variant="outline"
 className="flex-1 rounded-md"
 >
 Maybe Later
 </Button>
 </>
 )}
 </div>
 </div>

 {/* Footer */}
 <div className="px-6 py-4 bg-surface-muted border-t border-border">
 <p className="text-xs text-ink-tertiary text-center">
 {isPending 
 ? 'Review typically takes 1-3 business days. You\'ll receive an email notification once approved.'
 : 'Verification typically takes 1-3 business days. You\'ll receive an email notification once approved.'
 }
 </p>
 </div>
 </div>
 </ModalShell>
 );
};

export default VendorVerificationModal;
